import type { ErrorCopy } from '@shared/errors/catalog';

/**
 * The `error_code` values only this surface meets, in the `NOTIFICATION_`
 * namespace. Codes more than one feature meets live in
 * `@shared/errors/catalog.ts`.
 *
 * **Never inline a backend code in JSX.** The code is a log key; what reaches
 * the operator is the sentence, and the `X-Request-ID` is what they quote.
 *
 * Two of these describe a client that asked for something the service does not
 * offer — an unsupported sort, a selection past the configured bulk bound — and
 * both are `warning` rather than `danger`: nothing was destroyed, and the
 * operator has a next step. The bulk bound is the sharpest, because the way out
 * is «اختر أقلّ» and the copy has to say so rather than restating the refusal.
 *
 * The reserved upstream codes of the service's catalog are deliberately absent:
 * this service calls no sibling on any path, so `NOTIFICATION_UPSTREAM_FAILED`
 * and `NOTIFICATION_UPSTREAM_TIMEOUT` cannot be raised, and an entry for a code
 * that never arrives is copy nobody ever reads.
 */
export const errors: Readonly<Record<string, ErrorCopy>> = {
  NOTIFICATION_NOT_FOUND: {
    message: 'لم يعد هذا الإشعار في الصندوق. حدّث الصفحة لقراءة ما بقي.',
    tone: 'warning',
  },
  NOTIFICATION_BAD_INPUT: {
    message: 'تعذّرت قراءة هذه الصفحة. ارجع إلى أوّل الصندوق ثمّ أعد المحاولة.',
    tone: 'warning',
  },
  NOTIFICATION_SORT_COLUMN_NOT_ALLOWED: {
    message: 'هذا الترتيب غير متاح. اختر ترتيباً آخر من ضابط الفرز.',
    tone: 'warning',
  },
  NOTIFICATION_BULK_LIMIT_EXCEEDED: {
    message: 'الاختيار أكبر ممّا يقبله الإجراء دفعةً واحدة. اختر عدداً أقلّ ثمّ كرّر.',
    tone: 'warning',
  },
  NOTIFICATION_RATE_LIMITED: {
    message: 'طلبات كثيرة في وقت قصير. انتظر قليلاً ثمّ أعد المحاولة.',
    tone: 'warning',
  },
  NOTIFICATION_FORBIDDEN: {
    message: 'هذا الإجراء خارج صلاحيّتك.',
    tone: 'warning',
  },
  NOTIFICATION_NOT_READY: {
    message: 'الخدمة قيد الإقلاع. أعد المحاولة بعد قليل.',
    tone: 'neutral',
  },
  NOTIFICATION_CONFIGURATION_MISSING: {
    message: 'تعذّر تحميل الصندوق بسبب خلل في إعداد الخدمة. أبلغ الدعم.',
    tone: 'danger',
  },
  NOTIFICATION_DEFAULT_ROLE_MISSING: {
    message: 'تعذّر تحميل الصندوق بسبب خلل في إعداد الخدمة. أبلغ الدعم.',
    tone: 'danger',
  },
};
